import { useCallback, useRef, useState } from 'react'

interface UseDynamicStickyHeaderTitleOptions {
  /** The title to show in the header when scrolled (e.g. matter name). */
  title?: string
  /** Offset from top (px) at which the title appears in the sticky header. Defaults to 0. */
  scrollThreshold?: number
}

/**
 * Hook to show a title in a sticky header when the user has scrolled past the
 * in-page content that displays that title.
 * Resets when the observed element unmounts.
 */
export function useDynamicStickyHeaderTitle({ title, scrollThreshold = 0 }: UseDynamicStickyHeaderTitleOptions) {
  const [showTitle, setShowTitle] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const stickyHeaderTitleRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      if (!node) {
        setShowTitle(false)
        return
      }

      if (!title) return

      const observer = new IntersectionObserver(([entry]) => setShowTitle(!entry.isIntersecting), {
        threshold: 0,
        rootMargin: `-${scrollThreshold}px 0px 0px 0px`,
      })
      observer.observe(node)
      observerRef.current = observer
    },
    [title, scrollThreshold]
  )

  return { showTitle, stickyHeaderTitleRef }
}
